// Tap a logged row on the day timeline to edit or delete it. Type-aware:
// feed → amount, nurse → duration + side, sleep → start/end, diaper → kind.
// Every type can have its time corrected, and every entry can be deleted.

import { useState } from "react";
import type { EventRow } from "../lib/events";
import { useT, useAmount } from "../lib/LocaleContext";

type Props = {
  event: EventRow | null;
  onSave: (id: string, patch: Partial<EventRow>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
};

export function EditEventSheet({ event, onSave, onDelete, onClose }: Props) {
  if (!event) return null;
  // Keyed by id so each opened event gets a fresh, correctly-seeded form.
  return (
    <EditForm key={event.id} event={event} onSave={onSave} onDelete={onDelete} onClose={onClose} />
  );
}

function EditForm({
  event,
  onSave,
  onDelete,
  onClose,
}: {
  event: EventRow;
  onSave: (id: string, patch: Partial<EventRow>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const amount = useAmount();
  const [atTime, setAtTime] = useState(toTimeInput(event.at));
  const [endTime, setEndTime] = useState(toTimeInput(event.end_at));
  // `oz` stays the source of truth; `amtText` mirrors the field in the active unit.
  const [oz, setOz] = useState(event.amount_oz ?? 2);
  const [amtText, setAmtText] = useState(() => String(amount.value(event.amount_oz ?? 2)));
  const [durationMin, setDurationMin] = useState(event.duration_min ?? 15);
  const [side, setSide] = useState<"L" | "R" | "both">(event.side ?? "both");
  const [diaperKind, setDiaperKind] = useState<"wet" | "dirty" | "both">(event.diaper_kind ?? "wet");
  const [armed, setArmed] = useState(false);

  const title =
    event.type === "feed"
      ? t.editEvent.titleFeed
      : event.type === "nurse"
        ? t.editEvent.titleNurse
        : event.type === "sleep"
          ? t.editEvent.titleSleep
          : event.type === "diaper"
            ? t.editEvent.titleDiaper
            : t.editEvent.titleEntry;

  function handleSave() {
    const patch: Partial<EventRow> = { at: fromTimeInput(event.at, atTime) };
    if (event.type === "feed") patch.amount_oz = oz;
    if (event.type === "nurse") {
      patch.duration_min = durationMin;
      patch.side = side;
    }
    if (event.type === "sleep") {
      // The end time picker is time-only, so "6:00" on a sleep that started at
      // 11:40pm would land before the start. Roll it to the next day instead.
      patch.end_at = endTime ? endAfterStart(patch.at!, endTime) : null;
    }
    if (event.type === "diaper") patch.diaper_kind = diaperKind;
    onSave(event.id, patch);
    onClose();
  }

  function handleDelete() {
    if (!armed) {
      setArmed(true);
      return;
    }
    onDelete(event.id);
    onClose();
  }

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={styles.handle} />
        <h2 style={styles.title}>{title}</h2>

        <Field label={event.type === "sleep" ? t.editEvent.fieldStarted : t.editEvent.fieldTime}>
          <input type="time" style={styles.input} value={atTime} onChange={(e) => setAtTime(e.target.value)} />
        </Field>

        {event.type === "sleep" && (
          <Field label={t.editEvent.fieldEnded} hint={endTime ? undefined : t.editEvent.endedHint}>
            <input type="time" style={styles.input} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
          </Field>
        )}

        {event.type === "feed" && (
          <Field label={t.editEvent.fieldAmount(amount.word)}>
            <input
              type="number"
              inputMode="decimal"
              step={amount.unit === "ml" ? "5" : "0.5"}
              min="0"
              style={styles.input}
              value={amtText}
              onChange={(e) => {
                const raw = e.target.value;
                setAmtText(raw);
                const n = Number(raw);
                setOz(raw.trim() !== "" && Number.isFinite(n) ? amount.toOz(n) : 0);
              }}
            />
          </Field>
        )}

        {event.type === "nurse" && (
          <>
            <Field label={t.editEvent.fieldDuration}>
              <input
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                style={styles.input}
                value={durationMin}
                onChange={(e) => setDurationMin(Number(e.target.value) || 0)}
              />
            </Field>
            <Field label={t.editEvent.fieldSide}>
              <Seg
                options={[["L", t.editEvent.sideLeft], ["R", t.editEvent.sideRight], ["both", t.editEvent.sideBoth]]}
                value={side}
                onChange={setSide}
              />
            </Field>
          </>
        )}

        {event.type === "diaper" && (
          <Field label={t.editEvent.fieldKind}>
            <Seg
              options={[["wet", t.editEvent.kindWet], ["dirty", t.editEvent.kindDirty], ["both", t.editEvent.kindBoth]]}
              value={diaperKind}
              onChange={setDiaperKind}
            />
          </Field>
        )}

        <div style={styles.actions}>
          <button style={styles.ghost} onClick={onClose}>
            {t.editEvent.cancel}
          </button>
          <button style={styles.primary} onClick={handleSave}>
            {t.editEvent.save}
          </button>
        </div>
        <button
          style={{ ...styles.delete, color: armed ? "var(--bh-surface)" : "var(--bh-attn)", background: armed ? "var(--bh-attn)" : "transparent" }}
          onClick={handleDelete}
        >
          {armed ? t.editEvent.deleteArmed : t.editEvent.deleteIdle}
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={styles.field}>
      <div style={styles.fieldLabel}>{label}</div>
      {children}
      {hint && <div style={styles.hint}>{hint}</div>}
    </div>
  );
}

function Seg<T extends string>({
  options,
  value,
  onChange,
}: {
  options: [T, string][];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={styles.seg}>
      {options.map(([val, label]) => (
        <button
          key={val}
          style={{
            ...styles.segItem,
            background: val === value ? "var(--bh-ink)" : "var(--bh-surface-2)",
            color: val === value ? "var(--bh-surface)" : "var(--bh-ink-2)",
            fontWeight: val === value ? 600 : 500,
          }}
          onClick={() => onChange(val)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function toTimeInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function fromTimeInput(baseIso: string, hhmm: string): string {
  const d = new Date(baseIso);
  const [h, m] = hhmm.split(":").map(Number);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

// A sleep's end, given a time-only input. Anchored to the start's date, then
// rolled forward a day when that would put the end at or before the start —
// exactly the midnight-crossing case (asleep 11:40pm, woke 6:00am). Anchoring
// to the start rather than the old end also keeps a shortened night sleep from
// staying a day long.
function endAfterStart(startIso: string, hhmm: string): string {
  const end = new Date(fromTimeInput(startIso, hhmm));
  const start = new Date(startIso);
  if (end.getTime() <= start.getTime()) end.setDate(end.getDate() + 1);
  return end.toISOString();
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "oklch(0 0 0 / 0.55)",
    zIndex: 110,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  sheet: {
    background: "var(--bh-surface)",
    width: "100%",
    maxWidth: 520,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    padding: "10px 16px max(20px, env(safe-area-inset-bottom))",
    boxShadow: "0 -20px 60px oklch(0 0 0 / 0.4)",
    maxHeight: "92vh",
    overflowY: "auto",
  },
  handle: { width: 40, height: 4, background: "var(--bh-divider)", borderRadius: 2, margin: "0 auto 14px" },
  title: { fontSize: 20, fontWeight: 600, margin: "0 0 16px", color: "var(--bh-ink)" },
  field: { marginBottom: 14 },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--bh-ink-3)",
    marginBottom: 6,
  },
  hint: { fontSize: 12, color: "var(--bh-ink-3)", marginTop: 5 },
  input: {
    width: "100%",
    appearance: "none",
    background: "var(--bh-surface-2)",
    border: "1px solid var(--bh-divider)",
    borderRadius: 12,
    color: "var(--bh-ink)",
    fontSize: 17,
    fontFamily: "inherit",
    padding: "12px 12px",
    outline: "none",
    boxSizing: "border-box",
  },
  seg: { display: "flex", gap: 6 },
  segItem: {
    flex: 1,
    appearance: "none",
    border: "none",
    borderRadius: 12,
    padding: "12px 4px",
    fontSize: 15,
    cursor: "pointer",
    minHeight: 48,
  },
  actions: { display: "flex", gap: 8, marginTop: 18 },
  ghost: {
    flex: 1,
    background: "var(--bh-surface-2)",
    color: "var(--bh-ink-2)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 500,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
  primary: {
    flex: 2,
    background: "var(--bh-ink)",
    color: "var(--bh-surface)",
    border: "none",
    padding: "14px",
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 14,
    cursor: "pointer",
    minHeight: 56,
  },
  delete: {
    width: "100%",
    border: "none",
    padding: "12px",
    fontSize: 15,
    fontWeight: 600,
    borderRadius: 14,
    cursor: "pointer",
    marginTop: 8,
    minHeight: 48,
  },
};
